import React from "react";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { usePadding } from "@/components/utils/Padding";
import { Button } from "@/components/utils/Button";
import { Background, hexToRgba } from "@/components/utils/Background";
import BreadcrumbRevamp from "@/components/utils/BreadcrumbRevamp/BreadcrumbRevamp";
import "./styles.scss";

/* #META
 {
  "NAME": "HeroRevamp",
  "CLASSNAME": "hero-revamp-component",
  "URL": "http://nicasource.local/all"
 }
#ENDMETA */

const HeroRevamp = ({ data }) => {
  const module = data?.hero_revamp;

  if (!module) return null;

  const {
    background_component: backgroundComponent = {},
    breadcrumb_container: breadcrumbContainer = {},
    padding_component: customPadding,
    hero_content: heroContent = {},
  } = module;
  const { show_breadcrumbs } = breadcrumbContainer;
  const {
    button_component: buttonComponent = {},
    content,
    show_button_tags,
    show_secondary_button,
    buttons_alignment,
    secondary_button_component: secondaryButtonComponent = {},
    tags,
    tags_position,
  } = heroContent;

  //custom padding
  const paddingStyle = usePadding(customPadding);

  //background overlay
  const overlayOpts = module?.overlay_options ?? {};
  const overlayMode = overlayOpts?.overlay_mode || "gradient";
  const solidColor = overlayOpts?.overlay_solid_color || "#000000";
  const rawAlpha = overlayOpts?.overlay_solid_opacity;
  const solidAlpha = (() => {
    const n = Number(rawAlpha);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0.4;
  })();

  //tags
  const validTags = Array.isArray(tags)
    ? tags.map((t) => (t?.tag_text || "").trim()).filter(Boolean)
    : [];

  return (
    <section className="hero-revamp-component overflow-x-visible" style={paddingStyle}>
      <div>
        <Background asBackground {...backgroundComponent} />

        {overlayMode === "gradient" && (
          <div className="pointer-events-none absolute inset-0 -bottom-1 bg-[#00000066] bg-gradient-to-b from-transparent via-[#181728ac] to-[#181728]" />
        )}

        {overlayMode === "solid" && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: hexToRgba(solidColor, solidAlpha) }}
          />
        )}
      </div>

      <div className="container-revamp inner-container">
        <div className="breadcrumb-container">
          {show_breadcrumbs && (
            <div className="breadcrumb-container">
              <BreadcrumbRevamp showBreadcrumbs={!!show_breadcrumbs} />
            </div>
          )}
        </div>

        <div className="inner-content">
          {content && (
            <motion.div
              className="hero-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <div dangerouslySetInnerHTML={{ __html: content }} />
            </motion.div>
          )}

          {show_button_tags === "button" && !show_secondary_button ? (
            <Button fullWidth {...buttonComponent} />
          ) : (
            <div className="button-container" style={{ justifyContent: buttons_alignment }}>
              <Button fullWidth {...buttonComponent} />
              <Button fullWidth {...secondaryButtonComponent} />
            </div>
          )}

          {/* Tags mode */}
          {show_button_tags === "tags" && validTags.length > 0 && (
            <motion.div
              className="hero-tags"
              style={{ justifyContent: tags_position }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              {validTags.map((t, i) => (
                <span className="hero-tag" key={`tag-${i}`}>
                  {t}
                </span>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

HeroRevamp.propTypes = {
  data: PropTypes.shape({
    hero_revamp: PropTypes.shape({
      breadcrumb_container: PropTypes.shape({
        show_breadcrumbs: PropTypes.bool,
      }),
      hero_content: PropTypes.shape({
        content: PropTypes.string,
        show_button_tags: PropTypes.oneOf(["button", "tags"]),
        show_secondary_button: PropTypes.bool,
        buttons_alignment: PropTypes.oneOf(["flex-start", "center", "flex-end"]),
        button_component: PropTypes.object,
        button_component_display_button: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
        button_component_button_content: PropTypes.object,
        secondary_button_component_display_button: PropTypes.oneOfType([
          PropTypes.bool,
          PropTypes.any,
        ]),
        secondary_button_component_button_content: PropTypes.object,
        tags: PropTypes.arrayOf(
          PropTypes.shape({
            tag_text: PropTypes.string,
          })
        ),
        tags_position: PropTypes.oneOf(["flex-start", "center", "flex-end"]),
      }),
      background_component: PropTypes.object,
    }),
  }),
};

export default HeroRevamp;
